import { useEffect, useState } from "react";

/**
 * True when the device should not be asked to run continuous, GPU-heavy
 * decoration: touch/coarse pointers, narrow viewports, users who asked for
 * reduced motion, or devices reporting few CPU cores / low memory.
 *
 * Components use this to render a *static* version of an effect rather than
 * removing it — the theatre look survives, the per-frame cost does not.
 */
export const useLowPower = () => {
  const [lowPower, setLowPower] = useState(() => {
    if (typeof window === "undefined") return true;
    return evaluate();
  });

  useEffect(() => {
    const onChange = () => setLowPower(evaluate());
    const coarse = window.matchMedia?.("(pointer: coarse)");
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    coarse?.addEventListener?.("change", onChange);
    reduced?.addEventListener?.("change", onChange);
    window.addEventListener("resize", onChange, { passive: true });
    return () => {
      coarse?.removeEventListener?.("change", onChange);
      reduced?.removeEventListener?.("change", onChange);
      window.removeEventListener("resize", onChange);
    };
  }, []);

  return lowPower;
};

function evaluate() {
  const nav = navigator as Navigator & { deviceMemory?: number };
  const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  const narrow = window.innerWidth < 1024;
  const weakCpu = (nav.hardwareConcurrency ?? 8) <= 4;
  const lowMemory = (nav.deviceMemory ?? 8) <= 4;
  return coarse || reduced || narrow || weakCpu || lowMemory;
}

/**
 * Viewport-gated mount helper. Lets us render ONE navigation tree instead of
 * mounting both the mobile dock and the desktop bar and hiding one with CSS —
 * hidden nodes still cost layout, style recalc and animation frames.
 */
export const useIsDesktop = (query = "(min-width: 1024px)") => {
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && (window.matchMedia?.(query).matches ?? false)
  );
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [query]);
  return matches;
};
